// Window Email Helper
// This file contains helpers for embedding window images in emails

import { loadAllWindowImageBase64Chunks } from './windowImages';

interface WindowImageBase64Map {
  [key: string]: string;
}

// Cached map of window images to base64 data
let windowImagesBase64Map: WindowImageBase64Map | null = null;

// Use a tiny, guaranteed-to-work PNG for email clients
// This is a 100x100 pixel simple window outline
const FALLBACK_BASE64 = 'iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAIAAAD/gAIDAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4AkZCCQirr9/awAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAABeUlEQVR42u3csU3EMBSAYQ9BmyLdFRkgEio2OCbIKqnSsQM7ZIVUSeXTw0yQJ3SyY9nW/6XxKz59LznJzqJpGjIr7wQEFliCBRZYggUWWIIFlmCBJVhgCRZYgiVYYIElWGAJFlhgCRZYggWWYIEFlmCBJVhgCZZggSVYYIElWGAJFliCBZZgCRZYggWWYIEFlmCBJVhgCZZggSVYggWWYB2Z5Rm9W1XV/X779fHbOZ5lez/0+fnryzRNzLmPy7L83j8Pvry6Ocey3/ZN01w2L8y5j48vH6dpmnPv5yyC9TjdLoristvSNOf+vu/PsZRleXV9c7j/GIZBTmfM6bpusVgw53T6vq+qijmnU9c1c/5lR44cOe5Y/kIWWPafLLDAElNggQWWmAILLLDEFFhggSWmwJrPysGZlYOnGQ6eIHE2NnJ+yckiJ+xOyXVKz3J3lXvs3PPg3gmwrjHBAgssxxhMGUdYYIElWGCBJVhggSVYYIElWGCBJVhg8eQHGDI5bpQzGmIAAAAASUVORK5CYII=';

/**
 * Clean a path for base64 lookup by removing common prefixes
 */
export function cleanImagePath(path: string): string {
  if (!path) return '';
  
  // Remove any leading slash
  let cleaned = path.replace(/^\//, '');
  
  // Remove common prefixes
  cleaned = cleaned.replace(/^public\//i, '');
  cleaned = cleaned.replace(/^images\/windows\//i, '');
  cleaned = cleaned.replace(/^images\//i, '');
  cleaned = cleaned.replace(/^windows\//i, '');
  
  return cleaned;
}

/**
 * Extract the filename from a path
 */
export function getFilenameFromPath(path: string): string {
  if (!path) return '';
  return path.split('/').pop() || '';
}

/**
 * Load all window images as base64 data
 * This will try multiple methods to ensure we get data
 */
export async function loadWindowImagesAsBase64(): Promise<WindowImageBase64Map> {
  try {
    console.log('Loading window images as base64...');
    
    // First try the standard loader
    const windowImageBase64 = await loadAllWindowImageBase64Chunks();
    
    if (windowImageBase64 && Object.keys(windowImageBase64).length > 0) {
      console.log(`Loaded ${Object.keys(windowImageBase64).length} window images from standard loader`);
      console.log('Sample keys:', Object.keys(windowImageBase64).slice(0, 3));
      return windowImageBase64;
    }
    
    console.warn('Standard loader returned no data, using fallback...');
    
    // Create a fallback with some minimal data
    return createFallbackWindowImageData();
  } catch (error) {
    console.error('Error loading window images:', error);
    return createFallbackWindowImageData();
  }
}

/**
 * Create fallback image data for testing
 */
function createFallbackWindowImageData(): WindowImageBase64Map {
  console.log('Creating fallback window image data');
  
  const fallbackData: WindowImageBase64Map = {};
  
  // Use the same base64 fallback for ALL window images to ensure consistency
  const ALL_PATHS = [
    // Casements
    'casement',
    'Casement',
    'Casements',
    'CASEMENT',
    'Casements/Traditional',
    'Traditional',
    // Tilt-and-Turn
    'TiltAndTurn',
    'tilt-and-turn',
    'tiltandturn',
    'Tilt and Turn',
    // Other common window types
    'TopHung',
    'Fixed',
    'Sash',
    'Bay',
    'Bi-Fold',
    'French',
    'Patio',
    'Roof'
  ];
  
  // Add the FALLBACK_BASE64 for all these common prefixes
  ALL_PATHS.forEach(path => {
    fallbackData[path] = FALLBACK_BASE64;
    fallbackData[`${path}.jpg`] = FALLBACK_BASE64;
    fallbackData[`${path}.png`] = FALLBACK_BASE64;
    fallbackData[`/images/windows/${path}`] = FALLBACK_BASE64;
    fallbackData[`/images/windows/${path}.jpg`] = FALLBACK_BASE64;
    fallbackData[`/images/windows/${path}.png`] = FALLBACK_BASE64;
    
    // Add common variations
    fallbackData[path.toLowerCase()] = FALLBACK_BASE64;
    fallbackData[path.toUpperCase()] = FALLBACK_BASE64;
  });
  
  // Add all style numbers from 1-100
  for (let i = 1; i <= 100; i++) {
    fallbackData[`Style ${i}`] = FALLBACK_BASE64;
    fallbackData[`style ${i}`] = FALLBACK_BASE64;
    fallbackData[`Style${i}`] = FALLBACK_BASE64;
    fallbackData[`style${i}`] = FALLBACK_BASE64;
  }
  
  // Special handling for certain troublesome paths
  ['Button Opener Fixed Fan', 'Double Casement', 'Traditional Style One'].forEach(path => {
    fallbackData[path] = FALLBACK_BASE64;
    fallbackData[path.toLowerCase()] = FALLBACK_BASE64;
  });
  
  console.log(`Created fallback data with ${Object.keys(fallbackData).length} entries`);
  
  return fallbackData;
}

/**
 * Special explicit check for the Button Opener Fixed Fan
 */
function createButtonOpenerFixedFanFallback(): string {
  // Use the same fallback as the main constant for consistency
  return FALLBACK_BASE64;
}

// Helper function to convert an image URL to base64
async function convertImageToBase64(url: string): Promise<string> {
  try {
    // Make sure we have a full URL
    const fullUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
      
    console.log(`Converting image to base64: ${fullUrl}`);
    
    // Fetch the image
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    
    // Convert to blob then to base64
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Extract just the base64 part (remove the data:image/jpeg;base64, prefix)
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Error converting image to base64: ${url}`, error);
    return '';
  }
}

// Special paths for tilt-and-turn windows which often cause issues
const tiltAndTurnImagePaths: Record<number, string> = {
  1: '/images/windows/TiltAndTurn/Button Operation.jpg',
  2: '/images/windows/TiltAndTurn/Handle Operation.jpg', 
  3: '/images/windows/TiltAndTurn/Left Side-Hung.jpg',
  4: '/images/windows/TiltAndTurn/Right-Side Hung.jpg',
  5: '/images/windows/TiltAndTurn/Double Casement.jpg',
  6: '/images/windows/TiltAndTurn/Middle Bottom Opener.jpg',
  7: '/images/windows/TiltAndTurn/Traditional Style Bottom Openers.jpg',
  52: '/images/windows/TiltAndTurn/Button Opener Fixed Fan.jpg',
  54: '/images/windows/TiltAndTurn/Double Casement.jpg',
  59: '/images/windows/TiltAndTurn/Double Casement.jpg'
};

// Function to find base64 data for a window image
export async function findBase64ForWindowImage(imagePath: string): Promise<string> {
  if (!imagePath) {
    console.warn('No image path provided to findBase64ForWindowImage');
    return '';
  }
  
  try {
    // If a base64 data URI or raw base64 was provided directly (parametric case), normalize and return
    if (imagePath.startsWith('data:image')) {
      return imagePath.split(',')[1] || '';
    }
    if (/^[A-Za-z0-9+/=]+$/.test(imagePath) && imagePath.length > 100) {
      return imagePath;
    }

    // For tilt-and-turn windows, we may need to use a hardcoded path
    if (imagePath.includes('TiltAndTurn')) {
      // Try to extract style number
      const styleMatch = imagePath.match(/Style\s+(\d+)/i);
      if (styleMatch) {
        const styleNum = parseInt(styleMatch[1]);
        if (tiltAndTurnImagePaths[styleNum]) {
          console.log(`Using hardcoded tilt-and-turn path for style ${styleNum}`);
          return await convertImageToBase64(tiltAndTurnImagePaths[styleNum]);
        }
      }
    }
    
    // Otherwise, use the provided image path
    console.log(`Getting base64 for image: ${imagePath}`);
    return await convertImageToBase64(imagePath);
  } catch (error) {
    console.error('Error in findBase64ForWindowImage:', error);
    
    // Try a fallback image if the first one fails
    try {
      const fallbackImage = '/images/windows/Casements/Traditional/Traditional Style One.jpg';
      console.log(`Trying fallback image: ${fallbackImage}`);
      return await convertImageToBase64(fallbackImage);
    } catch (fallbackError) {
      console.error('Failed to get even the fallback image:', fallbackError);
      return '';
    }
  }
}

/**
 * Get a fallback image path for a window based on its type and style
 * This is used when all other methods fail
 */
export function getWindowFallbackImage(windowType: string, windowStyle: number): string {
  console.log(`Getting fallback image for ${windowType} style ${windowStyle}`);
  
  // Check if it's a tilt-and-turn window with a known style
  if (windowType?.toLowerCase().includes('tilt') && 
      windowType?.toLowerCase().includes('turn')) {
    
    // Use our hardcoded map
    if (tiltAndTurnImagePaths[windowStyle]) {
      return tiltAndTurnImagePaths[windowStyle];
    }
    
    // Default tilt-and-turn
    return '/images/windows/TiltAndTurn/Double Casement.jpg';
  }
  
  // Handle casement windows
  if (windowType?.toLowerCase().includes('casement')) {
    // Map styles to traditional casement images
    const styleNum = Math.min(5, Math.max(1, windowStyle % 5 || 1));
    return `/images/windows/Casements/Traditional/Traditional Style ${
      ['One', 'Two', 'Three', 'Four', 'Five'][styleNum - 1]
    }.jpg`;
  }
  
  // Generic fallback
  return '/images/windows/Casements/Traditional/Traditional Style One.jpg';
}